/**
 * useExperienceProfile — the client's single source for the organization's
 * resolved experience profile (CC-V35-BANK-PROFILE-01; accepted design
 * §5.5/§5.6/§5.10; D-098 tranche 4).
 *
 * The profile arrives on the EXISTING authenticated bootstrap
 * `GET /api/iam/me` (no second boot round trip, §5.5) and is resolved to a
 * client registry definition here. Server first, registry second, nothing
 * else: localStorage is NEVER a profile input, no query parameter or header
 * participates, and an unknown/drifted key resolves to 'unresolvable' — the
 * degraded shell — never to a substituted profile.
 *
 * The /me GET flows through useApiClient's cached GET path, whose cache keys
 * carry the organization epoch (`apiCache.getCacheKey`), so an organization
 * switch (§5.8 hard boundary) makes any cached bootstrap unreachable and the
 * next render re-fetches under the new organization.
 *
 * SAME-ORG WORKFLOW CHANGES (CC-V35-BANK-DEMO-READY-01): the hook also
 * subscribes to the experience-profile refresh boundary. When an
 * administrator changes the organization's workflow, every mounted consumer
 * drops to 'pending' (no wrong-product flash, no spurious denial) and
 * re-resolves against a FRESH /me — the refresh advanced the org epoch, so
 * the cached bootstrap is unreachable — all in the active session.
 */

import { useEffect, useRef, useState } from 'react';
import { useApiClient } from './useApiClient';
import {
  getExperienceProfileRefreshEpoch,
  subscribeExperienceProfileRefresh,
} from './experienceProfileRefresh';
import {
  resolveProfileDefinition,
  type ExperienceProfileDefinition,
} from '../config/experienceProfiles';

export type ExperienceProfileState =
  | { status: 'pending' }
  | { status: 'resolved'; definition: ExperienceProfileDefinition }
  | { status: 'disabled' }
  | { status: 'unresolvable' };

interface MeEnvelope {
  data?: {
    user?: {
      organization?: {
        id?: string;
        experienceProfile?: {
          key?: string;
          version?: number;
          status?: string;
        } | null;
      } | null;
    };
  };
}

export function useExperienceProfile(): ExperienceProfileState {
  // Depend on the destructured accessor, never the client object — the bare
  // object is a new identity every render and loops the effect.
  const { get, isAuthenticated } = useApiClient();
  const [state, setState] = useState<ExperienceProfileState>({ status: 'pending' });
  const [refreshEpoch, setRefreshEpoch] = useState(getExperienceProfileRefreshEpoch);
  const lastRefreshEpochRef = useRef(refreshEpoch);

  useEffect(
    () =>
      subscribeExperienceProfileRefresh(() => {
        setRefreshEpoch(getExperienceProfileRefreshEpoch());
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setState({ status: 'pending' });
      return;
    }

    if (lastRefreshEpochRef.current !== refreshEpoch) {
      // A workflow refresh occurred: drop to the neutral pending state
      // BEFORE the re-fetch, so no consumer keeps rendering (or denying on)
      // the outgoing experience while the new profile is in flight.
      lastRefreshEpochRef.current = refreshEpoch;
      setState({ status: 'pending' });
    }

    (async () => {
      try {
        const body = await get<MeEnvelope>('/api/iam/me');
        if (cancelled) return;

        const profile = body?.data?.user?.organization?.experienceProfile;
        if (!profile || typeof profile.key !== 'string' || typeof profile.version !== 'number') {
          // Pre-migration server, or the fail-closed server statuses with no
          // resolvable tuple: deny the shell rather than guess.
          setState({ status: 'unresolvable' });
          return;
        }
        if (profile.status === 'disabled') {
          setState({ status: 'disabled' });
          return;
        }
        if (profile.status === 'unresolvable') {
          setState({ status: 'unresolvable' });
          return;
        }
        try {
          const definition = resolveProfileDefinition(profile.key, profile.version);
          setState({ status: 'resolved', definition });
        } catch {
          // Server says active but this build's registry disagrees — the
          // §5.10 rollback drift, observed client-side. Deny, don't swap.
          setState({ status: 'unresolvable' });
        }
      } catch {
        if (!cancelled) {
          // Transient bootstrap failure: remain in the neutral boot state.
          // Rendering /home optimistically here is the exact wrong-product
          // flash §5.6 prohibits.
          setState({ status: 'pending' });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [get, isAuthenticated, refreshEpoch]);

  return state;
}
